import { useEffect, useId, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ChevronRight,
  Loader2,
  LockKeyhole,
  Search,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useHasPermission } from "../../../context/AuthContext";
import { cn } from "../../../lib/utils";
import {
  fetchRuleSettings,
  publishRuleSettings,
  saveRuleSettingsDraft,
  validateRuleSettings,
} from "../supervision-api";
import type {
  RuleConfiguration,
  RuleRegistryItem,
  RuleSettingsResponse,
  Severity,
  SupervisionRuleConfiguration,
} from "../types";

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

const severityLabel: Record<Severity, string> = {
  critical: "Critical",
  warn: "Warning",
  info: "Info",
};

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "The rule settings could not be updated.";

function RuleSwitch({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        "relative h-5 w-9 shrink-0 rounded-full transition",
        checked ? "bg-brand" : "bg-line",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-all",
          checked ? "left-[18px]" : "left-0.5",
        )}
      />
    </button>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  step,
  suffix,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  suffix?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  const inputId = useId();
  return (
    <label className="block" htmlFor={inputId}>
      <span className="mb-1.5 block text-[11px] font-medium text-ink-3">{label}</span>
      <div className="flex h-9 items-center rounded-lg border border-line bg-canvas focus-within:border-brand">
        <input
          id={inputId}
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          onChange={(event) => onChange(Number(event.target.value))}
          className="min-w-0 flex-1 bg-transparent px-3 text-xs text-ink outline-none disabled:cursor-not-allowed disabled:opacity-60"
        />
        {suffix ? <span className="pr-3 text-[10px] text-ink-3">{suffix}</span> : null}
      </div>
    </label>
  );
}

export function RuleSettingsPage() {
  const navigate = useNavigate();
  const canManage = useHasPermission("supervision.rules.manage");
  const [response, setResponse] = useState<RuleSettingsResponse | null>(null);
  const [working, setWorking] = useState<SupervisionRuleConfiguration | null>(null);
  const [selectedId, setSelectedId] = useState("");
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState<"load" | "save" | "publish" | "validate" | null>(
    canManage ? "load" : null,
  );
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!canManage) return;
    let active = true;
    void fetchRuleSettings()
      .then((next) => {
        if (!active) return;
        const initial = clone(next.draft ?? next.published);
        setResponse(next);
        setWorking(initial);
        setSelectedId(next.registry[0]?.id ?? "");
      })
      .catch((reason) => active && setError(errorMessage(reason)))
      .finally(() => active && setBusy(null));
    return () => {
      active = false;
    };
  }, [canManage]);

  const configError = response?.config_error ?? "";
  const frozen = Boolean(configError);
  const baseline = response?.draft ?? response?.published;
  const dirty = Boolean(
    working && baseline && JSON.stringify(working) !== JSON.stringify(baseline),
  );

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const visibleRules = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const rows = response?.registry ?? [];
    return needle
      ? rows.filter((item) =>
          `${item.label} ${item.group} ${item.description}`.toLowerCase().includes(needle),
        )
      : rows;
  }, [query, response?.registry]);

  const groups = useMemo(() => {
    const grouped = new Map<string, RuleRegistryItem[]>();
    visibleRules.forEach((item) => {
      grouped.set(item.group, [...(grouped.get(item.group) ?? []), item]);
    });
    return [...grouped.entries()];
  }, [visibleRules]);

  const selected = response?.registry.find((item) => item.id === selectedId) ?? null;
  const selectedSettings = selected && working ? working.rules[selected.id] : null;

  const updateRule = (updater: (current: RuleConfiguration) => RuleConfiguration) => {
    if (!selected || !working) return;
    setWorking({
      ...working,
      rules: { ...working.rules, [selected.id]: updater(working.rules[selected.id]) },
    });
    setNotice("");
    setError("");
  };

  const save = async () => {
    if (!response || !working) return null;
    setBusy("save");
    setError("");
    setNotice("");
    try {
      await validateRuleSettings(working);
      const next = await saveRuleSettingsDraft(response.version, working);
      setResponse(next);
      setWorking(clone(next.draft ?? next.published));
      setNotice("Draft saved. Live calls still use the published version.");
      return next;
    } catch (reason) {
      setError(errorMessage(reason));
      return null;
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    if (!response || !working) return;
    setBusy("publish");
    setError("");
    setNotice("");
    try {
      let base = response;
      if (dirty) {
        await validateRuleSettings(working);
        base = await saveRuleSettingsDraft(response.version, working);
      }
      const next = await publishRuleSettings(base.version);
      setResponse(next);
      setWorking(clone(next.published));
      setNotice(`Published version ${next.published_version}. New evaluations now use it.`);
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(null);
    }
  };

  const validate = async () => {
    if (!working) return;
    setBusy("validate");
    setError("");
    setNotice("");
    try {
      await validateRuleSettings(working);
      setNotice("Configuration is valid and safe to publish.");
    } catch (reason) {
      setError(errorMessage(reason));
    } finally {
      setBusy(null);
    }
  };

  if (!canManage) {
    return (
      <main className="flex min-h-0 flex-1 items-center justify-center bg-canvas p-6">
        <div className="max-w-sm rounded-2xl border border-line bg-elevated p-6 text-center shadow-sm">
          <LockKeyhole className="mx-auto mb-3 text-ink-3" size={24} />
          <h1 className="text-base font-semibold text-ink">Rule settings are restricted</h1>
          <p className="mt-1 text-xs leading-5 text-ink-3">
            Ask an organisation owner for the Supervision rule-management permission.
          </p>
          <button type="button" onClick={() => navigate("/supervision")} className="mt-4 text-xs font-medium text-brand">
            Back to live supervision
          </button>
        </div>
      </main>
    );
  }

  if (!response || !working) {
    if (error && busy !== "load") {
      return <main className="flex min-h-0 flex-1 items-center justify-center bg-canvas p-6"><div className="max-w-sm rounded-2xl border border-danger/30 bg-danger-soft p-5 text-center text-xs text-danger">{error}<button type="button" onClick={() => navigate("/supervision")} className="mt-3 block w-full font-semibold">Back to live supervision</button></div></main>;
    }
    return <main className="flex min-h-0 flex-1 items-center justify-center bg-canvas"><Loader2 className="animate-spin text-brand" /></main>;
  }

  return (
    <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-canvas">
      <header className="flex flex-wrap items-center gap-3 border-b border-line bg-elevated px-4 py-3">
        <button type="button" onClick={() => navigate("/supervision")} className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-ink-3 hover:text-ink" aria-label="Back to live supervision">
          <ArrowLeft size={15} />
        </button>
        <div>
          <h1 className="text-sm font-semibold text-ink">Alert rules</h1>
          <p className="text-[11px] text-ink-3">Supervision-only detection and risk policy</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={() => { setWorking(clone(response.draft ?? response.published)); setError(""); setNotice(""); }} disabled={!dirty || busy !== null} className="rounded-lg px-3 py-2 text-xs font-medium text-ink-3 hover:text-ink disabled:opacity-50">
            Discard
          </button>
          <button type="button" onClick={() => void validate()} disabled={busy !== null} className="rounded-lg border border-line px-3 py-2 text-xs font-medium text-ink-2 hover:text-ink disabled:opacity-50">
            Validate
          </button>
          <button type="button" onClick={() => void save()} disabled={frozen || !dirty || busy !== null} className="rounded-lg border border-line px-3 py-2 text-xs font-medium text-ink-2 hover:text-ink disabled:opacity-50">
            {busy === "save" ? "Saving…" : "Save draft"}
          </button>
          <button type="button" onClick={() => void publish()} disabled={frozen || (!dirty && !response.draft) || busy !== null} className="rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white shadow-sm disabled:opacity-50">
            {busy === "publish" ? "Publishing…" : "Publish changes"}
          </button>
        </div>
      </header>

      {frozen ? (
        <div role="alert" className="flex items-start gap-2 border-b border-warning/30 bg-warning-soft px-4 py-2 text-xs text-warning">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            The stored rules can't be read by this deployment ({configError}). Showing the defaults
            read-only so a save cannot overwrite the stored rules.
          </span>
        </div>
      ) : null}

      {(notice || error) ? (
        <div className={cn("flex items-center gap-2 border-b px-4 py-2 text-xs", error ? "border-danger/30 bg-danger-soft text-danger" : "border-line bg-soft text-ink-2")}>
          {error ? <AlertTriangle size={14} /> : <Check size={14} className="text-success" />}
          {error || notice}
        </div>
      ) : null}

      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto md:grid-cols-[310px_minmax(0,1fr)] md:overflow-hidden">
        <aside className="border-b border-line bg-elevated md:overflow-y-auto md:border-b-0 md:border-r">
          <div className="sticky top-0 z-10 space-y-3 border-b border-line bg-elevated p-4">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-ink-3" size={14} />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search rules…" className="h-9 w-full rounded-lg border border-line bg-canvas pl-9 pr-3 text-xs text-ink outline-none focus:border-brand" />
            </div>
            <div className="rounded-xl border border-line bg-canvas p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-[11px] font-semibold text-ink">Risk thresholds</span>
                <span className="text-[10px] text-ink-3">Published v{response.published_version}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <NumberField label="Amber" value={working.policy.amber_threshold} min={1} max={99} step={1} disabled={frozen} onChange={(value) => setWorking({ ...working, policy: { ...working.policy, amber_threshold: value } })} />
                <NumberField label="Red" value={working.policy.red_threshold} min={2} max={100} step={1} disabled={frozen} onChange={(value) => setWorking({ ...working, policy: { ...working.policy, red_threshold: value } })} />
              </div>
            </div>
          </div>
          <div className="p-2">
            {groups.map(([group, items]) => (
              <section key={group} className="mb-3">
                <h2 className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-3">{group}</h2>
                {items.map((item) => {
                  const settings = working.rules[item.id];
                  return (
                    <button key={item.id} type="button" onClick={() => setSelectedId(item.id)} className={cn("mb-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition", selectedId === item.id ? "bg-brand-soft text-brand" : "hover:bg-soft")}>
                      <span className={cn("h-2 w-2 shrink-0 rounded-full", settings.enabled ? settings.severity === "critical" ? "bg-danger" : settings.severity === "warn" ? "bg-warning" : "bg-ink-3" : "bg-line")} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1 text-xs font-medium text-ink">{item.label}{item.locked ? <LockKeyhole size={10} className="text-ink-3" /> : null}</span>
                        <span className="mt-0.5 block truncate text-[10px] text-ink-3">{settings.enabled ? `${severityLabel[settings.severity]} · ${settings.weight} risk` : "Disabled"}</span>
                      </span>
                      <ChevronRight size={13} className="text-ink-3" />
                    </button>
                  );
                })}
              </section>
            ))}
          </div>
        </aside>

        <section className="min-h-[520px] overflow-y-auto p-4 sm:p-6">
          {selected && selectedSettings ? (
            <div className="mx-auto max-w-3xl space-y-4">
              <div className="flex items-start gap-4 rounded-2xl border border-line bg-elevated p-5 shadow-sm">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-soft text-brand"><ShieldCheck size={18} /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-ink">{selected.label}</h2>
                    <span className="rounded-full border border-line px-2 py-0.5 text-[9px] uppercase tracking-wide text-ink-3">{selected.source}</span>
                    {selected.locked ? <span className="flex items-center gap-1 rounded-full bg-soft px-2 py-0.5 text-[9px] font-medium text-ink-3"><LockKeyhole size={9} /> Protected</span> : null}
                  </div>
                  <p className="mt-1 text-xs leading-5 text-ink-3">{selected.description}</p>
                </div>
                <RuleSwitch checked={selectedSettings.enabled} disabled={frozen || selected.locked} onChange={(enabled) => updateRule((current) => ({ ...current, enabled }))} />
              </div>

              {Object.keys(selected.parameter_schema ?? {}).length > 0 ? (
                <div className="rounded-2xl border border-line bg-elevated p-5 shadow-sm">
                  <h3 className="text-xs font-semibold text-ink">Detection</h3>
                  <p className="mt-1 text-[11px] text-ink-3">Only parameters consumed by this detector are available.</p>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {Object.entries(selected.parameter_schema ?? {}).map(([name, schema]) => (
                      <NumberField
                        key={name}
                        label={schema.label}
                        value={schema.suffix === "%" ? selectedSettings.parameters[name] * 100 : selectedSettings.parameters[name]}
                        min={schema.suffix === "%" ? schema.min * 100 : schema.min}
                        max={schema.suffix === "%" ? schema.max * 100 : schema.max}
                        step={schema.suffix === "%" ? schema.step * 100 : schema.step}
                        suffix={schema.suffix}
                        disabled={frozen || selected.locked || !selectedSettings.enabled}
                        onChange={(value) => updateRule((current) => ({
                          ...current,
                          parameters: {
                            ...current.parameters,
                            [name]: schema.suffix === "%" ? value / 100 : value,
                          },
                        }))}
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="rounded-2xl border border-line bg-elevated p-5 shadow-sm">
                <h3 className="text-xs font-semibold text-ink">Alert presentation</h3>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  {(["critical", "warn", "info"] as Severity[]).map((severity) => {
                    const belowMinimum = selected.minimum_severity && ["info", "warn", "critical"].indexOf(severity) < ["info", "warn", "critical"].indexOf(selected.minimum_severity);
                    return <button key={severity} type="button" disabled={frozen || selected.locked || belowMinimum || !selectedSettings.enabled} onClick={() => updateRule((current) => ({ ...current, severity }))} className={cn("rounded-xl border px-3 py-3 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40", selectedSettings.severity === severity ? severity === "critical" ? "border-danger/50 bg-danger-soft text-danger" : severity === "warn" ? "border-warning/50 bg-warning-soft text-warning" : "border-brand/40 bg-brand-soft text-brand" : "border-line text-ink-3 hover:text-ink")}>{severityLabel[severity]}</button>;
                  })}
                </div>
              </div>

              <div className="rounded-2xl border border-line bg-elevated p-5 shadow-sm">
                <h3 className="text-xs font-semibold text-ink">Risk contribution</h3>
                <p className="mt-1 text-[11px] leading-5 text-ink-3">Alert severity controls presentation. Risk determines the call’s Watch, Amber or Red state.</p>
                <div className="mt-4 grid items-end gap-4 sm:grid-cols-2">
                  <NumberField label="Score added when triggered" value={selectedSettings.weight} min={0} max={100} step={5} disabled={frozen || selected.locked || !selectedSettings.enabled} onChange={(weight) => updateRule((current) => ({ ...current, weight }))} />
                  <label className="flex h-9 items-center justify-between rounded-lg border border-line bg-canvas px-3 text-xs text-ink-2">
                    Immediate Red
                    <RuleSwitch checked={selectedSettings.immediate_red} disabled={frozen || selected.locked || !selectedSettings.enabled} onChange={(immediate_red) => updateRule((current) => ({ ...current, immediate_red }))} />
                  </label>
                </div>
              </div>

              {selected.id === "dead_air" ? <p className="rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-[11px] leading-5 text-warning">This detector measures transcript inactivity. It does not yet receive voice-activity events, so the label intentionally avoids claiming true audio silence.</p> : null}
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
